import type { Course, Organization } from "@prisma/client";
import type { CoachPeriods, RegisterRequest } from "src/service/AuthApi/types";
import { serverApi } from "src/service/serverApi";

import type { ResponseUserSettings } from "../../service/UsersApi/type";

export const authApi = serverApi.injectEndpoints({
  endpoints: (builder) => {
    return {
      getPaymentSync: builder.mutation<void, void>({
        query: () => {
          return {
            method: "POST",
            url: `payment/sync`,
          };
        },
      }),
      passwordReset: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { newPassword: string; token: string }
      >({
        query: (passwordReset) => {
          return {
            body: passwordReset,
            method: "PATCH",
            url: `auth/password_reset`,
          };
        },
      }),
      passwordResetRequest: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { email: string; role: string }
      >({
        query: (passwordResetRequest) => {
          return {
            body: passwordResetRequest,
            method: "post",
            url: `auth/password_reset_request`,
          };
        },
      }),
      postVerifyEmail: builder.mutation<
        {
          message: string;
          success: string;
        },
        { email: string }
      >({
        query: (address) => {
          return {
            body: address,
            method: "post",
            url: `auth/verify_email`,
          };
        },
      }),
      getUserSettings: builder.mutation<ResponseUserSettings, void>({
        query: () => {
          return {
            method: "get",
            url: `users/settings`,
          };
        },
      }),
      getInvitation: builder.mutation<
        {
          role: string;
          email: string;
          courseId?: string;
          course?: Course;
          organizationId?: string;
          organization?: Organization;
        },
        { stripped_Token: string }
      >({
        query: (invitationToken) => {
          return {
            params: { token: invitationToken.stripped_Token },
            method: "get",
            url: `auth/verify_invitation`,
          };
        },
      }),
      getPeriods: builder.mutation<
        {
          data: CoachPeriods[];
          pagination: {
            endCursor: string;
            hasNextPage: boolean;
          };
        },
        void
      >({
        query: () => {
          return {
            method: "get",
            url: `periods`,
          };
        },
      }),
      register: builder.mutation<void, RegisterRequest>({
        query: (RegisterItem) => {
          return {
            body: RegisterItem,
            method: "post",
            url: `auth/register/student`,
          };
        },
      }),
      registerCoach: builder.mutation<void, RegisterRequest>({
        query: (RegisterItem) => {
          return {
            body: RegisterItem,
            method: "post",
            url: `auth/register/coach`,
          };
        },
      }),
      getApprovalDetails: builder.mutation<
        ResponseUserSettings,
        { invitationToken: string }
      >({
        query: (invitationToken) => {
          return {
            body: invitationToken,
            method: "get",
            url: `users/settings`,
          };
        },
      }),
      upgrade: builder.mutation<void, RegisterRequest>({
        query: (RegisterItem) => {
          return {
            body: RegisterItem,
            method: "post",
            url: `auth/upgrade`,
          };
        },
      }),
      signUpEmail: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        {
          email: string;
          role: string;
          courseId?: string;
          organizationId?: string;
        }
      >({
        query: (signUpEmail) => {
          return {
            body: signUpEmail,
            method: "post",
            url: `auth/sign_up`,
          };
        },
      }),
      reactiveAccount: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { token: string }
      >({
        invalidatesTags: ["User"],
        query: (params) => {
          return {
            body: {
              token: params.token,
            },
            method: "PATCH",
            url: `auth/reactive-account`,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  usePasswordResetMutation,
  usePasswordResetRequestMutation,
  usePostVerifyEmailMutation,
  useGetInvitationMutation,
  useGetPeriodsMutation,
  useRegisterMutation,
  useRegisterCoachMutation,
  useGetUserSettingsMutation,
  useGetApprovalDetailsMutation,
  useSignUpEmailMutation,
  useUpgradeMutation,
  useGetPaymentSyncMutation,
  useReactiveAccountMutation,
} = authApi;
